import { compact } from 'lodash';
import * as z from 'zod';

import { USStrictAddress } from '../../Address';
import { DEFAULT_PAYEE_TAX_OPTION, PayeeInput, PayeePaymentMethod, PayeeTaxType, PayeeType } from '../../payee/Payee';
import { OptionalPhoneNumber } from '../../Phone';
import {
  ADDRESS_FIELD_KEYS,
  FLAT_FIELD_BY_ADDRESS_KEY,
  isAddressBlank,
  optionalDueMonthDay,
  optionalPostcode,
  optionalString,
  optionalTaxOption,
  preprocessTrim,
  rowToAddress,
} from './payeeRowPreprocessors';

export const BulkCreatePayeesRow = z
  .object({
    name: z.string().min(1),
    payeeType: PayeeType,
    taxType: PayeeTaxType.optional(),
    taxOption: optionalTaxOption,
    externalPayeeId: z.preprocess(preprocessTrim, z.string().min(1)),
    paymentMethod: PayeePaymentMethod.default('CHECK'),
    phone: OptionalPhoneNumber,
    addressLine1: optionalString,
    addressLine2: optionalString,
    addressLocality: optionalString,
    addressRegion: optionalString,
    addressPostcode: optionalPostcode,
    addressCountry: optionalString,
    dueDate1: optionalDueMonthDay,
    dueDate2: optionalDueMonthDay,
    dueDate3: optionalDueMonthDay,
    dueDate4: optionalDueMonthDay,
    isActive: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.payeeType === 'TAX') {
      if (!data.taxType) {
        ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Required for tax payees' });
      }
    } else {
      if (data.taxType !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Only allowed for tax payees' });
      }
      if (data.taxOption !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['taxOption'], message: 'Only allowed for tax payees' });
      }
      const dueDateKeys = ['dueDate1', 'dueDate2', 'dueDate3', 'dueDate4'] as const;
      for (const key of dueDateKeys) {
        if (data[key]) {
          ctx.addIssue({ code: 'custom', path: [key], message: 'Only allowed for tax payees' });
        }
      }
    }

    const anyAddressFilled = ADDRESS_FIELD_KEYS.some((key) => (data[key] ?? '').trim().length > 0);
    if (anyAddressFilled) {
      const result = USStrictAddress.safeParse(rowToAddress(data));
      if (!result.success) {
        for (const issue of result.error.issues) {
          const top = issue.path[0];
          const flatKey = typeof top === 'string' ? FLAT_FIELD_BY_ADDRESS_KEY[top] : undefined;
          ctx.addIssue({ ...issue, path: flatKey ? [flatKey] : issue.path });
        }
      }
    }
  });

export type BulkCreatePayeesRow = z.infer<typeof BulkCreatePayeesRow>;

export const BULK_CREATE_PAYEES_ROW_REQUIRED_FIELDS: ReadonlyArray<keyof BulkCreatePayeesRow> = [
  'name',
  'payeeType',
  'externalPayeeId',
];

export const bulkCreatePayeesRowToPayeeInput = (row: BulkCreatePayeesRow): PayeeInput => {
  const dueDates =
    row.payeeType === 'TAX'
      ? compact([row.dueDate1, row.dueDate2, row.dueDate3, row.dueDate4]).filter((d): d is string => d !== '')
      : [];

  return PayeeInput.parse({
    name: row.name,
    payeeType: row.payeeType,
    taxType: row.payeeType === 'TAX' ? row.taxType : undefined,
    taxOption: row.payeeType === 'TAX' ? (row.taxOption ?? DEFAULT_PAYEE_TAX_OPTION) : undefined,
    externalPayeeId: row.externalPayeeId,
    paymentMethod: row.paymentMethod,
    phone: row.phone || undefined,
    address: isAddressBlank(row) ? undefined : rowToAddress(row),
    dueDates,
    isActive: row.isActive,
  });
};
